import { useCallback } from 'react';
import PublicAPI from '../../../../../api/TreedisPublicDAO';
import { TAIConversation, useTAIChatStore } from '../../../../../store/useTAIChatStore';
import { ChatMessage } from '../MessageList';
import { TAIMessage } from './useTAIChat';

const CDN_BASE_URL = process.env.NEXT_PUBLIC_CDN || 'https://cdn.treedis.com';

export interface GetAllConversationsResponse {
  conversations: TAIConversation[];
}

export interface GetConversationResponse {
  id: number;
  tourId: number;
  sessionUuid: string;
  fingerprint: string;
  title: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
  messages: TAIMessage[];
}

function buildCdnUrl(path: string): string {
  if (!path) return path;

  // If it's already a full URL, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;

  // Construct CDN URL
  return `${CDN_BASE_URL}/${cleanPath}`;
}

export function useTAIConversations() {
  const setConversations = useTAIChatStore((state) => state.setConversations);
  const setConversationsLoading = useTAIChatStore((state) => state.setConversationsLoading);
  const setMessages = useTAIChatStore((state) => state.setMessages);
  const setCurrentConversationId = useTAIChatStore((state) => state.setCurrentConversationId);
  const setSessionId = useTAIChatStore((state) => state.setSessionId);

  const fetchConversations = useCallback(async (tourId: number): Promise<TAIConversation[] | null> => {
    setConversationsLoading(true);

    try {
      const response = await PublicAPI.getAllTAIConversations(
        {
          data: { tourId }
        },
        {
          isFingerprinted: true,
          turnOffToastError: true
        }
      );

      // Try different response structures
      let conversations: TAIConversation[] | null = null;

      if (response?.data?.conversations) {
        conversations = response.data.conversations as TAIConversation[];
      } else if (Array.isArray(response?.data)) {
        conversations = response.data as TAIConversation[];
      } else if (response?.conversations) {
        conversations = response.conversations as TAIConversation[];
      }

      if (conversations && conversations.length > 0) {
        setConversations(conversations);
        return conversations;
      }

      setConversations([]);
      return null;
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      return null;
    } finally {
      setConversationsLoading(false);
    }
  }, [setConversations, setConversationsLoading]);

  const loadConversation = useCallback(async (conversationId: number): Promise<boolean> => {
    try {
      const response = await PublicAPI.getTAIConversation(
        conversationId,
        {},
        {
          isFingerprinted: true,
          turnOffToastError: true
        }
      );

      if (response?.data) {
        const conversationData = response.data as GetConversationResponse;

        // Set the conversation ID and session ID
        setCurrentConversationId(conversationData.id);
        setSessionId(conversationData.sessionUuid);

        // Sort messages by createdAt to ensure correct order
        const sortedMessages = [...conversationData.messages].sort((a, b) => {
          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateA - dateB;
        });

        // Convert backend messages to ChatMessage format
        const chatMessages: ChatMessage[] = sortedMessages.map((msg) => {
          const hasMedia = msg.mediaAttachments && msg.mediaAttachments.length > 0;
          // Check for image media - mediaType can be "img" or start with "image/"
          const hasImageMedia = hasMedia && msg.mediaAttachments!.some(m =>
            m.mediaType === 'img' || m.mediaType.startsWith('image/')
          );

          let screenshotUrl: string | undefined;
          if (hasImageMedia) {
            const firstImage = msg.mediaAttachments![0];

            // Use presignedDownloadUrl if available
            if (firstImage.presignedDownloadUrl) {
              screenshotUrl = firstImage.presignedDownloadUrl;
            }
            // Use downloadUrl with CDN if available
            else if (firstImage.downloadUrl) {
              screenshotUrl = buildCdnUrl(firstImage.downloadUrl);
            }
            // Construct CDN URL from fileName
            else if (firstImage.fileName) {
              const path = `tours/${conversationData.tourId}/conversation/${conversationData.id}/${firstImage.fileName}`;
              screenshotUrl = buildCdnUrl(path);
            }
          }

          return {
            id: `${msg.id || Date.now()}-${msg.role}`,
            role: msg.role,
            text: msg.content,
            raw: hasMedia ? {
              mediaAttachments: msg.mediaAttachments,
              isScreenshotQuery: hasImageMedia,
              screenshot: screenshotUrl
            } : undefined,
          };
        });

        setMessages(chatMessages);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to load conversation:', error);
      return false;
    }
  }, [setMessages, setCurrentConversationId, setSessionId]);

  return {
    fetchConversations,
    loadConversation,
  };
}

